import { create } from 'zustand';
import { appConfig } from '../config/app-config';
import { clock } from '../time/clock';
import { isUnsavedWorkEmpty } from './unsaved-work';
import { reloadPage } from './reload-page';
import { noServiceWorker, type WorkerUpdates } from './service-worker';
import { CACHE_PREFIX } from './worker-protocol';

/** The shortest gap between two version checks (021 SC-008). */
const CHECK_INTERVAL_MS = 30_000;

/**
 * How long a taken update is waited on before the page reloads anyway (issue 231).
 *
 * An activation that works is measured at about a second. One that has stalled stays stalled for
 * minutes, so this only has to be comfortably longer than the first — and being wrong in the short
 * direction costs nothing: the page reloads a moment before `controllerchange` would have told it to,
 * and leaving the page is itself what lets the activation finish.
 */
export const ACTIVATION_PATIENCE_MS = 3_000;

/** No second stalled reload inside this long of the first — see `stalledReloadIsAllowed`. */
const STALLED_RELOAD_INTERVAL_MS = 60_000;

/**
 * Whether a newer version is waiting, and what to do about it (021 FR-011, FR-013, FR-014,
 * FR-018, FR-029, FR-031).
 *
 * The problem this exists to solve is created by installability itself. A browser tab picks up a
 * new version whenever it is reloaded; an installed window is one nobody ever reloads, and the
 * worker will keep serving the bundle it has. So the application has to notice for itself — and
 * CD ships to `develop` on every merge, eleven times in one working day on 2026-09-02.
 *
 * Checked EAGERLY, on every navigation and on tab focus. Without that the offer appears whenever
 * the worker next decides to ask, and the practitioner's existing reflex — reload to pick up a
 * fix — has been made unreliable by the worker with nothing dependable offered in its place.
 * That is SC-008's whole subject. It is deliberately separate from WHEN THE WORKER IS REGISTERED,
 * which is delayed to keep it off the first-paint path (`service-worker.ts`); confusing the two
 * produces a client that takes half a minute to admit it is out of date.
 *
 * "Newer" means DIFFERENT FROM WHAT IS SERVED, not numerically greater (FR-031), which is what
 * makes a rollback converge downward exactly as a release converges upward. A service worker
 * already has that shape — the browser compares the worker's bytes, and Workbox's precache
 * compares each entry's revision — so this store does not add a comparison of its own.
 *
 * ---
 *
 * **034: an `@Injectable` with four private signals becomes a MODULE-PRIVATE Zustand store, the
 * shape `offline-presentation.ts` already uses.** The privacy argument is the same one and it
 * matters here too: a Zustand store object exposes `setState`, so exporting it would let any caller
 * declare a version ready — which is the whole of what this store is trusted to know.
 *
 * The one genuine difference is WHERE THE WIRING LIVES. Angular's constructor subscribed to
 * `SwUpdate` and to the router, and `DestroyRef` unsubscribed; a module has no constructor and no
 * lifetime shorter than the document's, so `startUpdateWatch` is called once from `main.tsx` and
 * `onNavigationSettled` is called by the root component on each completed navigation. Nothing is
 * torn down, because nothing outlives the page these are registered on.
 */
interface UpdateState {
  /** A newer version has downloaded and is waiting to be activated. */
  ready: boolean;
  applying: boolean;
  dismissed: boolean;
}

const store = create<UpdateState>()(() => ({ ready: false, applying: false, dismissed: false }));

const select = <T,>(pick: (s: UpdateState) => T) => ({
  use: (): T => store(pick),
  get: (): T => pick(store.getState()),
});

export const updateReady = select((s) => s.ready);

/**
 * What the banner binds to — the WHOLE predicate, so the component has one term and not three.
 *
 * FR-029's dismissal is in here rather than left to the caller: an earlier version stopped at
 * `ready && !applying`, so the banner had to read this store twice and re-AND the dismissal
 * itself. A predicate named `offered` that does not answer "is it offered" is an invitation to
 * get that conjunction wrong somewhere else later.
 *
 * `applying` is deliberately NOT exported on its own, for the same reason: a public accessor would
 * invite the next banner variant to re-AND the conjunction by hand — the mistake `offered` exists
 * to prevent.
 */
export const updateOffered = select((s) => s.ready && !s.applying && !s.dismissed);

export function dismissUpdate(): void {
  store.setState({ dismissed: true });
}

/**
 * The worker, and the document to reload — the two things Angular injected.
 *
 * `noServiceWorker` until `startUpdateWatch` says otherwise, so a unit test that never calls it
 * gets a store which correctly believes there is no worker, and `document` so that the reload seam
 * is substitutable exactly as the injected `DOCUMENT` was: a test must be able to supply a document
 * whose reload does not take the test runner with it.
 */
let updates: WorkerUpdates = noServiceWorker;
let doc: Document = globalThis.document;
let lastCheck = 0;
let recovering = false;

/**
 * Wire the store to a running worker. Called once, from `main.tsx`.
 *
 * Angular did this in the store's constructor, which the root injector ran exactly once per
 * application. The call site moving is what makes it possible to hand a fake in — which is the same
 * thing `TestBed.configureTestingModule` did and is not a weakening of it.
 */
export function startUpdateWatch(source: WorkerUpdates, hostDocument = globalThis.document): void {
  updates = source;
  doc = hostDocument;
  if (!updates.isEnabled) return;

  updates.onVersionReady(() => store.setState({ ready: true }));

  /**
   * FR-018. A practitioner has no way to know their stored copy is corrupt and no reason to know
   * that clearing site data is the fix — left unhandled the application is permanently unopenable
   * for them and looks like a server outage. Recovery is silent (FR-032): they see a load, not an
   * error about their browser.
   */
  updates.onUnrecoverable(() => void recover());

  const onFocus = (): void => {
    if (doc.visibilityState === 'visible') void check();
  };
  doc.addEventListener('visibilitychange', onFocus);
}

/**
 * Take the new version now — what the banner's button calls.
 *
 * Guarded by `applying` for the reason 020 established with `/import`: a control that cannot
 * cancel what it appears to cancel must not be offered, and a second activation while one is in
 * flight is a state nobody has reasoned about. Exclusion rather than reconciliation.
 */
export function applyUpdate(): Promise<void> {
  return apply('asked');
}

/**
 * `asked` is the practitioner pressing the button; `unasked` is FR-013's backstop. They differ in
 * exactly one place, the end of the patience below.
 */
async function apply(how: 'asked' | 'unasked'): Promise<void> {
  const { applying, ready } = store.getState();
  if (applying || !ready) return;
  store.setState({ applying: true });
  try {
    /**
     * Issue 231 — the wait is bounded, and a stall is answered in two steps.
     *
     * `activateUpdate()` resolves on `controllerchange`, and Chromium can leave that pending for
     * minutes. To hand over it stops the old worker; a request from this page arriving in the same
     * instant restarts the old worker, and the handover is abandoned until a five-minute timer.
     * Measured through the DevTools protocol: the old worker goes stopping, stopped, starting,
     * running, and the new one stays installed. Sending the message again does nothing, and neither
     * does `registration.update()`.
     *
     * What releases it is a navigation: within milliseconds of a reload being asked for, the old
     * worker is redundant and the new one is activating. But THAT reload does not complete. It was
     * started while the old worker was being torn down, and it was still pending ninety seconds
     * later with the new worker long since active. A reload started after the new worker is active
     * does complete (23 stuck runs in 23).
     *
     * So: when the patience runs out, reload to release the handover, go on waiting for the
     * activation exactly as before, and reload again when it arrives. If the first reload does
     * complete, the page is gone and nothing after it runs.
     */
    const activation = updates.activateUpdate();
    let patience: ReturnType<typeof setTimeout> | undefined;
    const outcome = await Promise.race([
      activation.then(() => 'activated' as const),
      new Promise<'stalled'>((resolve) => { patience = setTimeout(() => resolve('stalled'), ACTIVATION_PATIENCE_MS); }),
    ]);
    clearTimeout(patience);

    if (outcome === 'stalled') {
      if (mayReloadOverAStall(how)) {
        noteStalledReload();
        reloadPage(doc);
      }
      await activation;
    }
    reloadPage(doc);
  } catch {
    /* fall through to the reset below */
  }

  // Cleared unconditionally, INCLUDING after a reload was requested. `location.reload()` is a
  // request, not a guarantee — `defaultView` can be null, and a reload can be refused — and if
  // it does not happen, leaving `applying` set removes the offer for the life of the page with
  // no way to retry and nothing said. When the reload does happen this line never runs, so the
  // only case it affects is the one that would otherwise strand the practitioner.
  store.setState({ applying: false });
}

/**
 * Whether a stalled activation may be answered with the reload that releases it, or must simply be
 * waited out as it was before issue 231.
 *
 * Two reasons to wait it out:
 *
 * - **FR-012.** The backstop applies an update unasked only while nothing is half-typed, and it
 *   checked that before it started. Work begun during the wait would be destroyed by a reload the
 *   practitioner never asked for. The button is different: pressing it is the asking.
 * - **A reload loop.** If a stalled reload did not release the update, the next page finds the same
 *   worker waiting, applies it, stalls and reloads again — every few seconds, for ever. So one
 *   stalled reload per `STALLED_RELOAD_INTERVAL_MS`.
 */
function mayReloadOverAStall(how: 'asked' | 'unasked'): boolean {
  if (how === 'unasked' && !isUnsavedWorkEmpty()) return false;
  return stalledReloadIsAllowed();
}

/**
 * Session storage, because the fact has to survive the reload it describes and nothing else: it is
 * about this window, and a new window starts clean.
 */
const stalledReloadKey = (): string => `${appConfig().storagePrefix}update.stalledReloadAt`;

function stalledReloadIsAllowed(): boolean {
  try {
    const at = Number(sessionStorage.getItem(stalledReloadKey()));
    return !Number.isFinite(at) || at === 0 || clock().getTime() - at >= STALLED_RELOAD_INTERVAL_MS;
  } catch {
    // Storage that cannot be read cannot hold a loop guard either; a reload is still the right answer
    // to a stall, and `applying` being reset on the next page is the only brake left.
    return true;
  }
}

function noteStalledReload(): void {
  try {
    sessionStorage.setItem(stalledReloadKey(), String(clock().getTime()));
  } catch {
    /* not being able to remember it is not a reason to withhold the reload */
  }
}

async function check(): Promise<void> {
  if (!updates.isEnabled || store.getState().ready) return;

  /**
   * Throttled, because "eager" does not have to mean "every time".
   *
   * A version check fetches the worker script and re-validates the precache manifest, so it is a
   * round trip no 304 can shorten. Unthrottled it fired on every in-app navigation and every tab
   * focus — ten screens in a minute bought ten identical requests, for a document that changes when
   * someone deploys. `/simplify` measured it.
   *
   * 30 seconds keeps SC-008 comfortably (its promise is one navigation or one tab focus, and a
   * practitioner who has been on a page longer than half a minute is the normal case).
   */
  const now = clock().getTime();
  if (now - lastCheck < CHECK_INTERVAL_MS) return;
  lastCheck = now;
  // A check that cannot reach the server is not a failure worth showing anyone.
  try {
    await updates.checkForUpdate();
  } catch {
    /* offline, or the server is unreachable: ask again at the next navigation */
  }
}

/**
 * FR-013: apply a pending version by ourselves at the next point NOTHING IS HALF-TYPED.
 *
 * Both halves matter. Without the backstop, a practitioner who ignores the offer runs a stale
 * bundle indefinitely — 015's failure shape made durable by the worker. Without the
 * unsaved-work check, the backstop is a machine for destroying edits, which FR-012 forbids.
 *
 * 034: called by `app-root.tsx` on each completed navigation, where Angular's store subscribed to
 * `Router.events` and filtered for `NavigationEnd` itself. The router is not injectable here, so
 * the one component that sits inside it does the telling.
 */
export async function onNavigationSettled(): Promise<void> {
  await check();

  // A dismissal SUSPENDS the backstop, it does not merely hide the banner.
  //
  // The first version cleared `dismissed` on navigation and then auto-applied, so pressing
  // "Not now" and clicking any link reloaded the page anyway — a button that cannot decline what
  // it appears to decline. That is the same rule this file already quotes 020 for, broken by the
  // file that quotes it. Found by `/code-review`.
  //
  // FR-014 still holds: the offer is not lost, it is deferred. `dismissUpdate()` lasts until the
  // practitioner asks for the version explicitly, or until the next launch — and every launch is
  // a fresh store, so a dismissal can never outlive the session that made it.
  if (store.getState().dismissed) return;

  if (store.getState().ready && isUnsavedWorkEmpty()) await apply('unasked');
}

/**
 * FR-018/FR-032. The worker's stored copy is broken beyond its own repair.
 *
 * It CLEARS and then reloads. An earlier version only reloaded, while `evidence.md` claimed it
 * cleared — reloading without clearing hands the same broken caches back to the same worker, so
 * the recovery was a no-op that read as one. `/code-review` found the documentation and the code
 * disagreeing.
 *
 * Once per page. A worker that keeps reporting this state would otherwise produce an unbounded
 * silent reload loop, which is a worse failure than the one being recovered from.
 *
 * 034: the prefix was `ngsw:`, Angular's worker's own naming. It is now `CACHE_PREFIX`, which
 * `src/sw.ts` hands to Workbox's `setCacheNameDetails` — one constant, imported by both ends,
 * because a prefix that matched nothing would leave this sweeping no caches and reporting success.
 */
async function recover(): Promise<void> {
  if (recovering) return;
  recovering = true;

  try {
    const keys = await caches.keys();
    await Promise.all(keys.filter((key) => key.startsWith(CACHE_PREFIX)).map((key) => caches.delete(key)));
  } catch {
    // Best effort: clearing may be refused, and the reload is still worth attempting.
  }

  reloadPage(doc);
}

/**
 * Test seam, like the other stores'. Production code never calls it.
 *
 * `TestBed.inject` handed every Angular case a fresh instance; module state outlives a test, so
 * without this one case's ready version — or its throttle — is what the next case starts from.
 */
export function resetUpdateStore(): void {
  store.setState({ ready: false, applying: false, dismissed: false });
  updates = noServiceWorker;
  doc = globalThis.document;
  lastCheck = 0;
  recovering = false;
}
